import { Status } from '@common/enums/global.enums'
import { CertificationTrainerValues } from '@common/types/certification.types'
import { Option } from '@common/types/global.types'
import { DashboardTrainee, DashboardTraineeFormValues, FiltersFormValues, Trainee, TraineeFormValues } from '@common/types/person.types'
import { get, patch, post } from '@common/utils/request.utils'

type PersonApiRequests = {
	getTrainees: (page: number, filters: FiltersFormValues) => Promise<DashboardTrainee[]>
	getCertificationTrainers: (traineeId: string, values: CertificationTrainerValues) => Promise<Option[]>
	getPersonById: (id: string) => Promise<Trainee>
	createTrainee: (values: DashboardTraineeFormValues) => Promise<DashboardTrainee>
	updateTrainee: (id: string, values: TraineeFormValues) => Promise<Trainee>
	deleteTrainee: (id: string) => Promise<Trainee>
}

const PERSON_API_URL = '/persons'

/**
 * Person api requests
 */
export const usePersonApi = (): PersonApiRequests => {
	const getTrainees = (page: number, filters: FiltersFormValues): Promise<DashboardTrainee[]> => {
		const params = new URLSearchParams({ page: `${page}` })
		Object.entries(filters).forEach(([key, value]) => {
			if (value) {
				if (value instanceof Date) {
					params.append(key, value.toISOString())

				} else {
					params.append(key, `${value}`)
				}
			}
		})
		return get(`${PERSON_API_URL}?${params}`)
	}

	const getCertificationTrainers = (traineeId: string, values: CertificationTrainerValues): Promise<Option[]> => {
		const { obtaining: { isTrainer }, ...other } = values
		const params = new URLSearchParams({ ...other, traineeId, isTrainer: `${isTrainer}` })
		return get(`${PERSON_API_URL}/certifications-trainers?${params}`)
	}

	const getPersonById = (id: string): Promise<Trainee> => {
		return get(`${PERSON_API_URL}/${id}`)
	}

	const createTrainee = (values: DashboardTraineeFormValues): Promise<DashboardTrainee> => {
		const { company, ...other } = values
		const body = {
			...other,
			companyId: company
		}

		return post(
			PERSON_API_URL,
			JSON.stringify(body)
		)
	}

	const updateTrainee = (id: string, values: TraineeFormValues): Promise<Trainee> => {
		const { company, ...other } = values
		const body = {
			...other,
			companyId: company.value
		}

		return patch(
			`${PERSON_API_URL}/${id}`,
			JSON.stringify(body)
		)
	}

	const updateTraineeStatus = (id: string, status: Status): Promise<Trainee> => {
		return patch(
			`${PERSON_API_URL}/${id}/status`,
			JSON.stringify({ status })
		)
	}

	const deleteTrainee = (id: string): Promise<Trainee> => {
		return updateTraineeStatus(id, Status.DELETED)
	}

	return {
		getTrainees,
		getCertificationTrainers,
		getPersonById,
		createTrainee,
		updateTrainee,
		deleteTrainee
	}
}
